"use client";

import { useEffect, useMemo, useState } from "react";
import { usePathname } from "next/navigation";
import AppDownloadBadges from "@/components/common/AppDownloadBadges";
import {
  APP_INSTALL_PROMPT_DISMISSED_KEY,
  buildRoaveAppUrl,
  getMobilePlatform,
  getStoreUrlForPlatform,
  shouldSuppressAppInstallPrompt,
  type MobilePlatform,
} from "@/utils/appDownload";

const EXIT_INTENT_STORAGE_KEY = "roave_app_download_prompt_seen_at";
const EXIT_INTENT_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

function hasRecentExitIntentPrompt() {
  try {
    const seenAt = Number(window.localStorage.getItem(EXIT_INTENT_STORAGE_KEY));
    return Number.isFinite(seenAt) && Date.now() - seenAt < EXIT_INTENT_COOLDOWN_MS;
  } catch {
    return false;
  }
}

function rememberExitIntentPrompt() {
  try {
    window.localStorage.setItem(EXIT_INTENT_STORAGE_KEY, String(Date.now()));
  } catch {
    // localStorage can be unavailable in private browsing; the prompt still works.
  }
}

function hasDismissedMobilePrompt() {
  try {
    return window.localStorage.getItem(APP_INSTALL_PROMPT_DISMISSED_KEY) === "true";
  } catch {
    return false;
  }
}

function dismissMobilePrompt() {
  try {
    window.localStorage.setItem(APP_INSTALL_PROMPT_DISMISSED_KEY, "true");
  } catch {
    // Ignore storage failures; the visible prompt still closes for this page view.
  }
}

function detectPlatform(): MobilePlatform {
  return getMobilePlatform(
    navigator.userAgent || navigator.vendor || "",
    navigator.platform,
    navigator.maxTouchPoints,
  );
}

export default function AppDownloadExitIntent() {
  const pathname = usePathname();
  const [exitIntentOpen, setExitIntentOpen] = useState(false);
  const [showMobilePrompt, setShowMobilePrompt] = useState(false);
  const [platform, setPlatform] = useState<MobilePlatform>("desktop");

  useEffect(() => {
    const nextPlatform = detectPlatform();
    const shouldShowPrompt =
      nextPlatform !== "desktop" &&
      !shouldSuppressAppInstallPrompt(pathname) &&
      !hasDismissedMobilePrompt();

    setPlatform(nextPlatform);
    setShowMobilePrompt(shouldShowPrompt);
  }, [pathname]);

  useEffect(() => {
    if (platform !== "desktop" || shouldSuppressAppInstallPrompt(pathname) || hasRecentExitIntentPrompt()) {
      return;
    }

    let triggered = false;
    const openPrompt = () => {
      if (triggered) return;
      triggered = true;
      rememberExitIntentPrompt();
      setExitIntentOpen(true);
    };

    const handleMouseOut = (event: MouseEvent) => {
      if (event.clientY <= 8 && !event.relatedTarget) openPrompt();
    };

    document.addEventListener("mouseout", handleMouseOut);

    return () => {
      document.removeEventListener("mouseout", handleMouseOut);
    };
  }, [pathname, platform]);

  const appUrl = useMemo(() => {
    if (typeof window === "undefined") return "roave://";
    return buildRoaveAppUrl(window.location.pathname, window.location.search);
  }, [pathname]);
  const storeUrl = getStoreUrlForPlatform(platform);

  const closeMobilePrompt = () => {
    dismissMobilePrompt();
    setShowMobilePrompt(false);
  };

  return (
    <>
      {showMobilePrompt && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="mobile-app-download-prompt-title"
          className="roave-mobile-app-prompt"
          onClick={closeMobilePrompt}
        >
          <div
            className="roave-mobile-app-prompt__panel"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="roave-mobile-app-prompt__close"
              aria-label="Close app download prompt"
              onClick={closeMobilePrompt}
            >
              ×
            </button>
            <div className="roave-mobile-app-prompt__brand" aria-hidden="true">
              <span>R</span>
            </div>
            <span className="roave-mobile-app-prompt__eyebrow">Travel with perks</span>
            <h2 id="mobile-app-download-prompt-title">Roave works even better in the app</h2>
            <p>
              Search hotels faster, save private deals, and continue booking with a smoother
              mobile experience.
            </p>
            <div className="roave-mobile-app-prompt__actions">
              <a className="roave-mobile-app-prompt__open" href={appUrl}>
                Open app
              </a>
              <a
                className="roave-mobile-app-prompt__download"
                href={storeUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Download app
              </a>
            </div>
            <button
              type="button"
              className="roave-mobile-app-prompt__later"
              onClick={closeMobilePrompt}
            >
              Continue on web
            </button>
          </div>
        </div>
      )}

      {exitIntentOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="app-download-prompt-title"
          className="roave-app-prompt"
          onClick={() => setExitIntentOpen(false)}
        >
          <div className="roave-app-prompt__panel" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className="roave-app-prompt__close"
              aria-label="Close app download prompt"
              onClick={() => setExitIntentOpen(false)}
            >
              ×
            </button>
            <span className="roave-app-prompt__eyebrow">Private app deals</span>
            <h2 id="app-download-prompt-title">Unlock lower member rates in the Roave app</h2>
            <p>
              Download Roave for discounted app pricing, private hotel deals, and member-only
              savings you will not always see on the public web.
            </p>
            <AppDownloadBadges style={{ justifyContent: "center" }} />
            <button
              type="button"
              className="roave-app-prompt__later"
              onClick={() => setExitIntentOpen(false)}
            >
              Maybe later
            </button>
            <span className="roave-app-prompt__fineprint">Private rates and discounts vary by hotel and date.</span>
          </div>
        </div>
      )}

      <style jsx>{`
        .roave-mobile-app-prompt {
          position: fixed;
          inset: 0;
          z-index: 2147483000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 18px;
          background: rgba(3, 40, 67, 0.34);
          backdrop-filter: blur(7px);
        }

        .roave-mobile-app-prompt__panel {
          position: relative;
          width: min(100%, 390px);
          border: 1px solid rgba(213, 200, 181, 0.9);
          border-radius: 24px;
          padding: 30px 22px 22px;
          background:
            radial-gradient(circle at top left, rgba(201, 168, 121, 0.2), transparent 42%),
            #fffaf4;
          color: #08233a;
          box-shadow: 0 26px 70px rgba(3, 40, 67, 0.28);
          text-align: center;
        }

        .roave-mobile-app-prompt__close {
          position: absolute;
          top: 12px;
          right: 12px;
          width: 34px;
          height: 34px;
          border: 0;
          border-radius: 50%;
          background: rgba(3, 40, 67, 0.08);
          color: #08233a;
          font-size: 24px;
          line-height: 1;
        }

        .roave-mobile-app-prompt__brand {
          display: grid;
          width: 54px;
          height: 54px;
          margin: 0 auto 14px;
          place-items: center;
          border-radius: 17px;
          background: #08233a;
          box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.08);
        }

        .roave-mobile-app-prompt__brand span {
          color: #c9a879;
          font-size: 24px;
          font-weight: 800;
          letter-spacing: -0.03em;
        }

        .roave-mobile-app-prompt__eyebrow {
          color: #9c7d5c;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.12em;
          text-transform: uppercase;
        }

        .roave-mobile-app-prompt h2 {
          max-width: 290px;
          margin: 8px auto 10px;
          color: #08233a;
          font-size: 25px;
          line-height: 1.15;
          font-weight: 800;
          letter-spacing: -0.03em;
        }

        .roave-mobile-app-prompt p {
          max-width: 310px;
          margin: 0 auto 18px;
          color: #536274;
          font-size: 14px;
          line-height: 1.55;
        }

        .roave-mobile-app-prompt__actions {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
        }

        .roave-mobile-app-prompt__open,
        .roave-mobile-app-prompt__download {
          display: inline-flex;
          min-height: 48px;
          align-items: center;
          justify-content: center;
          border-radius: 999px;
          padding: 0 14px;
          font-size: 14px;
          font-weight: 800;
          line-height: 1;
          text-decoration: none;
        }

        .roave-mobile-app-prompt__open {
          background: #08233a;
          color: #fff;
        }

        .roave-mobile-app-prompt__download {
          border: 1px solid rgba(156, 125, 92, 0.42);
          background: #fff;
          color: #08233a;
        }

        .roave-mobile-app-prompt__later {
          margin-top: 14px;
          border: 0;
          background: transparent;
          color: #7b8794;
          font-size: 13px;
          font-weight: 700;
          text-decoration: underline;
          text-underline-offset: 3px;
        }

        .roave-app-prompt {
          position: fixed;
          inset: 0;
          z-index: 2147483000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          background: rgba(15, 23, 42, 0.48);
          backdrop-filter: blur(8px);
        }

        .roave-app-prompt__panel {
          position: relative;
          width: min(100%, 460px);
          border-radius: 20px;
          padding: 34px 28px 30px;
          background: #fff;
          color: #1b2430;
          box-shadow: 0 28px 70px rgba(15, 23, 42, 0.24);
          text-align: center;
        }

        .roave-app-prompt__close {
          position: absolute;
          top: 12px;
          right: 14px;
          width: 34px;
          height: 34px;
          border: 0;
          border-radius: 50%;
          background: #f3f4f6;
          color: #1b2430;
          font-size: 24px;
          line-height: 1;
        }

        .roave-app-prompt__eyebrow {
          display: inline-flex;
          margin-bottom: 12px;
          color: #9c7d5c;
          font-size: 12px;
          font-weight: 800;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }

        .roave-app-prompt h2 {
          margin: 0;
          font-size: 28px;
          line-height: 1.15;
          font-weight: 800;
        }

        .roave-app-prompt p {
          max-width: 360px;
          margin: 14px auto 20px;
          color: #5d6a7a;
          font-size: 15px;
          line-height: 1.6;
        }

        .roave-app-prompt__later {
          display: inline-flex;
          margin-top: 14px;
          border: 0;
          background: transparent;
          color: #5d6a7a;
          font-size: 14px;
          font-weight: 700;
          text-decoration: underline;
          text-underline-offset: 3px;
        }

        .roave-app-prompt__fineprint {
          display: block;
          margin-top: 10px;
          color: #8a96a8;
          font-size: 12px;
          line-height: 1.4;
        }

        @media (min-width: 769px) {
          .roave-mobile-app-prompt {
            display: none;
          }
        }

        @media (max-width: 520px) {
          .roave-mobile-app-prompt {
            align-items: flex-end;
            padding: 12px;
            padding-bottom: max(12px, env(safe-area-inset-bottom));
          }

          .roave-mobile-app-prompt__panel {
            width: 100%;
            border-radius: 22px;
          }

          .roave-mobile-app-prompt h2 {
            font-size: 23px;
          }

          .roave-app-prompt {
            align-items: flex-end;
            padding: 12px;
          }

          .roave-app-prompt__panel {
            border-radius: 18px;
            padding: 30px 20px 24px;
          }

          .roave-app-prompt h2 {
            font-size: 24px;
          }
        }
      `}</style>
    </>
  );
}
